import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const SERVER_COLORS = {
  MENA: '#f97316', FR: '#3b82f6', IT: '#22c55e', DE: '#a855f7',
};

const fmtD = (n) => {
  if (!n && n !== 0) return '—';
  return n?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const fmtUSD = (n) => {
  if (n == null) return '—';
  return '$' + parseFloat(n).toFixed(2);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '8px 12px', fontSize: '12px' }}>
      <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>{label}</p>
      <p style={{ color: 'white', fontWeight: '600' }}>💎 {fmtD(payload?.[0]?.value)}</p>
    </div>
  );
};

const ActivitySection = ({ currentMonth, tiktok_id, server, loading, acConfig }) => {
  const { t } = useTranslation();
  const serverColor = SERVER_COLORS?.[server] || '#E5202A';
  const [dailyData, setDailyData] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(true);

  useEffect(() => {
    if (!tiktok_id) return;
    const fetchDaily = async () => {
      setDailyLoading(true);
      try {
        const period = currentMonth?.period ||
          `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`;
        const [pY, pM] = period.split('-').map(Number);
        const lastDay = new Date(pY, pM, 0).getDate();
        const { data, error } = await supabase
          .from('creator_daily')
          .select('date, diamonds, live_duration_minutes')
          .eq('tiktok_id', tiktok_id)
          .gte('date', `${period}-01`)
          .lte('date', `${period}-${String(lastDay).padStart(2, '0')}`)
          .order('date', { ascending: true });
        if (!error && data) setDailyData(data);
      } catch (err) {
        console.error('Daily fetch error:', err);
      } finally {
        setDailyLoading(false);
      }
    };
    fetchDaily();
  }, [tiktok_id, currentMonth?.period]);

  const stats = useMemo(() => {
    if (currentMonth) {
      return {
        diamonds: currentMonth?.diamonds || 0,
        liveDays: currentMonth?.live_days || 0,
        liveHours: parseFloat(((currentMonth?.live_duration_minutes || 0) / 60).toFixed(1)),
      };
    }
    if (!dailyData?.length) return { diamonds: 0, liveDays: 0, liveHours: 0 };
    const diamonds = dailyData.reduce((s, r) => s + (r?.diamonds || 0), 0);
    const liveDays = dailyData.filter(r => (r?.live_duration_minutes || 0) > 0).length;
    const liveHours = dailyData.reduce((s, r) => s + (r?.live_duration_minutes || 0), 0) / 60;
    return { diamonds, liveDays, liveHours: parseFloat(liveHours.toFixed(1)) };
  }, [currentMonth, dailyData]);

  const activenessProgress = useMemo(() => {
    const currentLevel = currentMonth?.activeness_level ?? 0;
    const currentDays = stats?.liveDays;
    const currentHours = stats?.liveHours;
    const currentDiamonds = stats?.diamonds;

    if (!acConfig || acConfig.length === 0) {
      return { currentLevel, currentDays, currentHours, nextLevelConfig: null, isMaxLevel: false, currentBonusCalc: null, estimatedBonusNext: null };
    }

    const maxLevel = Math.max(...acConfig.map(c => c.level));
    const isMaxLevel = currentLevel >= maxLevel;
    const nextLevelConfig = acConfig.find(c => c.level === currentLevel + 1) || null;

    let daysNeeded = 0, hoursNeeded = 0, daysPct = 100, hoursPct = 100;
    let estimatedBonusNext = null;

    if (nextLevelConfig) {
      daysNeeded = Math.max(0, (nextLevelConfig?.min_live_days || 0) - currentDays);
      hoursNeeded = Math.round(Math.max(0, (nextLevelConfig?.min_live_hours || 0) - currentHours) * 10) / 10;
      daysPct = nextLevelConfig?.min_live_days > 0
        ? Math.min(100, Math.round((currentDays / nextLevelConfig.min_live_days) * 100)) : 100;
      hoursPct = nextLevelConfig?.min_live_hours > 0
        ? Math.min(100, Math.round((currentHours / nextLevelConfig.min_live_hours) * 100)) : 100;
      if (nextLevelConfig?.bonus_ratio != null) {
        estimatedBonusNext = parseFloat((currentDiamonds / 100 * nextLevelConfig.bonus_ratio / 100).toFixed(2));
      }
    }

    const currentBonusCalc = (() => {
      const cfg = acConfig.find(c => c.level === currentLevel);
      if (!cfg) return currentMonth?.bonus_activeness ?? null;
      return parseFloat((currentDiamonds / 100 * (cfg.bonus_ratio || 0) / 100).toFixed(2));
    })();

    return {
      currentLevel, currentDays, currentHours, nextLevelConfig, isMaxLevel,
      daysNeeded, hoursNeeded, daysPct, hoursPct,
      estimatedBonusNext, currentBonusCalc,
    };
  }, [acConfig, currentMonth, stats]);

  const tierProjections = useMemo(() => {
    if (!currentMonth) return null;
    const tc = currentMonth?.tier_current || 1;
    const tm = currentMonth?.tier_m1 || 1;
    let tierStatus;
    if (tc > tm) tierStatus = 'Ranked Up';
    else if (tc === tm && tm >= 2) tierStatus = 'Maintained';
    else if (tc < tm) tierStatus = 'Downgraded';
    else tierStatus = null;
    return {
      tierCurrent: tc,
      tierM1: tm,
      bonusRankup: currentMonth?.bonus_rankup || 0,
      tierStatus,
    };
  }, [currentMonth]);

  const chartData = useMemo(() => {
    return dailyData?.map(r => ({
      date: r?.date ? r.date.slice(5) : '',
      diamonds: r?.diamonds || 0,
    })) || [];
  }, [dailyData]);

  const periodLabel = currentMonth?.period || '—';
  const isLoading = loading || dailyLoading;

  const card = (children) => (
    <div style={{ background: '#111111', border: '1px solid #1f1f1f', borderRadius: '12px', padding: '14px' }}>
      {children}
    </div>
  );

  const label = (text) => (
    <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif', marginBottom: '10px', margin: '0 0 10px 0' }}>
      {text}
    </p>
  );

  const divider = <div style={{ borderTop: '1px solid #1f1f1f', margin: '8px 0' }} />;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[...Array(4)].map((_, i) => (
          <div key={i} style={{ height: '80px', background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px' }} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* ROW 1: Activité mois */}
      {card(
        <>
          {label(t('creatorDetail.activity', { period: periodLabel }))}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {[
              { l: t('creatorDetail.diamonds'), v: fmtD(stats?.diamonds), c: 'white' },
              { l: t('creatorDetail.liveDays'), v: stats?.liveDays, c: 'white' },
              { l: t('creatorDetail.hours'), v: `${stats?.liveHours}h`, c: 'white' },
              {
                l: t('creatorDetail.cashback'),
                v: currentMonth?.cashback_amount > 0 ? fmtUSD(currentMonth?.cashback_amount) : '—',
                sub: currentMonth?.cashback_pct > 0 ? `${currentMonth?.cashback_pct}%` : null,
                c: '#22c55e'
              },
            ].map(({ l, v, c, sub }) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' }}>{l}</div>
                <div style={{ fontSize: '15px', fontWeight: '500', color: c, fontFamily: 'Sora, sans-serif' }}>
                  {v}
                  {sub && <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginLeft: '3px' }}>{sub}</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ROW 2: Objectif créateur */}
      {currentMonth?.objective_diamonds > 0 && card((() => {
        const diamonds = stats?.diamonds;
        const obj = currentMonth?.objective_diamonds;
        const pct = obj > 0 ? Math.min(100, (diamonds / obj) * 100) : 0;
        const manque = Math.max(0, obj - diamonds);
        const projection = currentMonth?.projected_diamonds;
        const pctColor = pct >= 100 ? '#22c55e' : pct >= 70 ? '#f97316' : '#ef4444';
        return (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              {label(t('creatorDetail.creatorObjective'))}
              <span style={{ fontSize: '13px', fontWeight: '500', color: pctColor }}>{pct.toFixed(1)}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
              <span style={{ fontSize: '18px', fontWeight: '500', color: 'white', fontFamily: 'Sora, sans-serif' }}>{fmtD(diamonds)} 💎</span>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>/ {fmtD(obj)}</span>
            </div>
            <div style={{ background: '#1f1f1f', borderRadius: '4px', height: '6px', marginBottom: '6px', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: pctColor, borderRadius: '4px' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                {manque > 0
                  ? <><span>{t('creatorDetail.missing')} </span><span style={{ color: pctColor, fontWeight: '500' }}>{fmtD(manque)} 💎</span></>
                  : <span style={{ color: '#22c55e' }}>{t('creatorDetail.objectiveReached')}</span>
                }
              </span>
              {projection > 0 && (
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                  {t('creatorDetail.projection')} : {projection >= 1000000 ? `${(projection/1000000).toFixed(1)}M` : projection >= 1000 ? `${(projection/1000).toFixed(0)}K` : projection}
                </span>
              )}
            </div>
          </>
        );
      })())}

      {/* ROW 3: Tier + Activeness côte à côte */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>

        {/* Tier */}
        {card(
          <>
            {label(t('creatorDetail.tierLabel'))}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              {[{ l: t('creatorDetail.tierM1'), v: tierProjections?.tierM1 }, null, { l: t('creatorDetail.tierCurrent'), v: tierProjections?.tierCurrent }].map((item, i) =>
                item === null ? (
                  <span key={i} style={{ color: 'rgba(255,255,255,0.3)' }}>→</span>
                ) : (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '3px' }}>{item.l}</div>
                    <span style={{
                      fontSize: '13px', fontWeight: '500', padding: '3px 8px', borderRadius: '6px',
                      background: item.v >= 7 ? 'rgba(239,68,68,0.15)' : item.v >= 5 ? 'rgba(249,115,22,0.15)' : item.v >= 3 ? 'rgba(234,179,8,0.15)' : 'rgba(107,114,128,0.15)',
                      color: item.v >= 7 ? '#ef4444' : item.v >= 5 ? '#f97316' : item.v >= 3 ? '#eab308' : '#9ca3af',
                      border: `0.5px solid ${item.v >= 7 ? 'rgba(239,68,68,0.3)' : item.v >= 5 ? 'rgba(249,115,22,0.3)' : item.v >= 3 ? 'rgba(234,179,8,0.3)' : 'rgba(107,114,128,0.3)'}`,
                    }}>
                      {item.v != null ? `T${item.v}` : '—'}
                    </span>
                  </div>
                )
              )}
            </div>
            {tierProjections?.tierStatus && (
              <span style={{
                fontSize: '10px', fontWeight: '500', padding: '2px 6px', borderRadius: '4px',
                background: tierProjections.tierStatus === 'Ranked Up' ? 'rgba(34,197,94,0.15)' : tierProjections.tierStatus === 'Maintained' ? 'rgba(59,130,246,0.15)' : 'rgba(239,68,68,0.15)',
                color: tierProjections.tierStatus === 'Ranked Up' ? '#22c55e' : tierProjections.tierStatus === 'Maintained' ? '#3b82f6' : '#ef4444',
                border: `0.5px solid ${tierProjections.tierStatus === 'Ranked Up' ? 'rgba(34,197,94,0.3)' : tierProjections.tierStatus === 'Maintained' ? 'rgba(59,130,246,0.3)' : 'rgba(239,68,68,0.3)'}`,
              }}>
                {tierProjections.tierStatus === 'Ranked Up' ? t('creatorDetail.tierUp') : tierProjections.tierStatus === 'Maintained' ? t('creatorDetail.tierMaintain') : tierProjections.tierStatus}
              </span>
            )}
            {divider}
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '2px' }}>
              {tierProjections?.tierStatus === 'Ranked Up' ? t('creatorDetail.bonusRankup') : t('creatorDetail.bonusMaintain')}
            </div>
            <div style={{ fontSize: '16px', fontWeight: '500', color: '#f97316', fontFamily: 'Sora, sans-serif' }}>
              {tierProjections?.bonusRankup > 0 ? fmtUSD(tierProjections.bonusRankup) : '—'}
            </div>
          </>
        )}

        {/* Activeness */}
        {card(
          <>
            {label(t('creatorDetail.activenessLabel'))}

            {/* Niveau actuel + bonus actuel */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '12px' }}>
              <div style={{ fontSize: '26px', fontWeight: '500', color: activenessProgress?.currentLevel > 0 ? '#22c55e' : 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                L{activenessProgress?.currentLevel}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '2px' }}>
                  {t('creatorDetail.currentBonus')} (L{activenessProgress?.currentLevel})
                </div>
                <div style={{ fontSize: '15px', fontWeight: '500', color: activenessProgress?.currentBonusCalc > 0 ? '#22c55e' : 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
                  {activenessProgress?.currentBonusCalc > 0 ? fmtUSD(activenessProgress.currentBonusCalc) : '—'}
                </div>
              </div>
            </div>

            {activenessProgress?.isMaxLevel ? (
              <div style={{ fontSize: '10px', color: '#FFD700', marginBottom: '8px' }}>{t('creatorDetail.maxLevelReached')}</div>
            ) : activenessProgress?.nextLevelConfig ? (
              <>
                {/* Ligne jours */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                      {t('creatorDetail.days')}{' '}
                      {activenessProgress.daysNeeded > 0
                        ? <span style={{ color: '#f97316' }}>— {t('creatorDetail.missingDays', { count: activenessProgress.daysNeeded })}</span>
                        : <span style={{ color: '#22c55e' }}>— L{activenessProgress.currentLevel + 1} ✓</span>
                      }
                    </span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
                      {activenessProgress.currentDays} / {activenessProgress.nextLevelConfig.min_live_days}{t('admin.dashboard.daysShort', 'd')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '3px', marginBottom: '3px' }}>
                    {[1,2,3,4,5].map(l => {
                      const threshold = acConfig.find(c => c.level === l);
                      const reached = threshold ? activenessProgress.currentDays >= (threshold.min_live_days || 0) : false;
                      const isCurrent = l === activenessProgress.currentLevel + 1;
                      return (
                        <div key={l} style={{
                          flex: 1, height: '5px', borderRadius: '3px',
                          background: reached ? '#22c55e' : isCurrent ? '#f97316' : 'rgba(255,255,255,0.12)'
                        }} />
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    {[1,2,3,4,5].map(l => {
                      const threshold = acConfig.find(c => c.level === l);
                      const reached = threshold ? activenessProgress.currentDays >= (threshold.min_live_days || 0) : false;
                      const isCurrent = l === activenessProgress.currentLevel + 1;
                      return (
                        <div key={l} style={{ flex: 1, textAlign: 'center', fontSize: '9px', fontWeight: reached ? '500' : '400',
                          color: reached ? '#22c55e' : isCurrent ? '#f97316' : 'rgba(255,255,255,0.3)' }}>
                          L{l}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Ligne heures */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                      {t('creatorDetail.hours')}{' '}
                      {activenessProgress.hoursNeeded > 0
                        ? <span style={{ color: '#f97316' }}>— {t('creatorDetail.missingHours', { count: activenessProgress.hoursNeeded })}</span>
                        : <span style={{ color: '#22c55e' }}>— L{activenessProgress.currentLevel + 1} ✓</span>
                      }
                    </span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
                      {activenessProgress.currentHours} / {activenessProgress.nextLevelConfig.min_live_hours}h
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '3px', marginBottom: '3px' }}>
                    {[1,2,3,4,5].map(l => {
                      const threshold = acConfig.find(c => c.level === l);
                      const reached = threshold ? activenessProgress.currentHours >= (threshold.min_live_hours || 0) : false;
                      const isCurrent = l === activenessProgress.currentLevel + 1;
                      return (
                        <div key={l} style={{
                          flex: 1, height: '5px', borderRadius: '3px',
                          background: reached ? '#22c55e' : isCurrent ? '#f97316' : 'rgba(255,255,255,0.12)'
                        }} />
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    {[1,2,3,4,5].map(l => {
                      const threshold = acConfig.find(c => c.level === l);
                      const reached = threshold ? activenessProgress.currentHours >= (threshold.min_live_hours || 0) : false;
                      const isCurrent = l === activenessProgress.currentLevel + 1;
                      return (
                        <div key={l} style={{ flex: 1, textAlign: 'center', fontSize: '9px', fontWeight: reached ? '500' : '400',
                          color: reached ? '#22c55e' : isCurrent ? '#f97316' : 'rgba(255,255,255,0.3)' }}>
                          L{l}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : null}

            {/* Bonus si prochain niveau atteint */}
            {!activenessProgress?.isMaxLevel && activenessProgress?.estimatedBonusNext != null && (
              <div style={{ borderTop: '1px solid #1f1f1f', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                  {t('creatorDetail.ifLevelReached', { level: activenessProgress.currentLevel + 1 })}
                </span>
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#3b82f6', fontFamily: 'Sora, sans-serif' }}>
                  {fmtUSD(activenessProgress.estimatedBonusNext)}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* ROW 4: Bonus total estimé */}
      {card(
        <>
          {label(t('creatorDetail.totalBonusEstimated'))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{t('creatorDetail.rankupMaintain')}</span>
              <span style={{ fontSize: '12px', fontWeight: '500', color: '#f97316' }}>
                {tierProjections?.bonusRankup > 0 ? fmtUSD(tierProjections.bonusRankup) : '—'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{t('creatorDetail.activenessLabel')}</span>
              <span style={{ fontSize: '12px', fontWeight: '500', color: '#22c55e' }}>
                {activenessProgress?.currentBonusCalc != null ? fmtUSD(activenessProgress.currentBonusCalc) : '—'}
              </span>
            </div>
            <div style={{ borderTop: '1px solid #1f1f1f', paddingTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', fontWeight: '500', color: 'white' }}>Total</span>
              <span style={{ fontSize: '15px', fontWeight: '500', color: 'white', fontFamily: 'Sora, sans-serif' }}>
                {fmtUSD((tierProjections?.bonusRankup || 0) + (activenessProgress?.currentBonusCalc || 0))}
              </span>
            </div>
          </div>
        </>
      )}

      {/* ROW 5: Courbe diamonds par jour */}
      {card(
        <>
          {label(t('creatorDetail.diamondsPerDay', { period: periodLabel }))}
          {chartData?.length === 0 ? (
            <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '12px' }}>
              {t('common.noData')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} axisLine={false} tickLine={false}
                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="diamonds" stroke={serverColor} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: serverColor }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </>
      )}

    </div>
  );
};

export default ActivitySection;